import { PlayerManager } from 'chromecast-caf-receiver/cast.framework';
import { EventType } from 'chromecast-caf-receiver/cast.framework.events';
import { StreamType } from 'chromecast-caf-receiver/cast.framework.messages';
import type { CastSession } from '../types/session';
import { Hermes } from '../network';
import { Logger } from '../logger';
import { prefetchAndQueueNext } from './queue-manager';

/**
 * Media status — handles media completion signaling.
 *
 * Ported from `legacy/js/receiver.js` (lines 377-384). Responsible for:
 * - Calling `update_ended` mutation on Hermes when playback finishes (`ENDED` event)
 * - Validating that the media is VOD (bypass for LIVE) and has valid IDs
 *
 * **Note on interceptors:** The `MEDIA_STATUS`, `EDIT_AUDIO_TRACKS`, and
 * `EDIT_TRACKS_INFO` message interceptors listed under seção 4.6 in
 * REFACTOR_TASKS.md are implemented in `tracks-handler.ts` (seção 4.5),
 * since they are tightly coupled with the track enumeration and preference
 * logic. This module handles only the `completeMedia()` / `ENDED` flow.
 *
 * **Differences from legacy:**
 * - Uses typed Hermes SDK (`Hermes.client.UpdateEnded`) instead of XHR
 * - Reads mediaId/playlistId from the stored `loadRequestData` on session
 * - Structured logging via `Logger`
 */

const LOG_TAG = 'MEDIA_STATUS';

// ---------------------------------------------------------------------------
// Metadata typing (matches load-interceptor.ts SenderCustomData shape)
// ---------------------------------------------------------------------------

interface BPCustomData {
  mediaId?: string;
  playlistId?: string;
  [key: string]: unknown;
}

interface BPMetadata {
  customData?: BPCustomData;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Core: completeMedia
// ---------------------------------------------------------------------------

/**
 * Notifies Hermes that the current media has finished playback.
 *
 * Validates:
 * - `loadRequestData` exists on the session
 * - Stream is not LIVE
 * - `mediaId` and `playlistId` are present in customData
 *
 * Called on the `ENDED` event from the player manager.
 */
function completeMedia(session: CastSession): void {
  const request = session.loadRequestData;
  if (!request?.media) return;

  // Bypass for LIVE streams
  if (request.media.streamType === StreamType.LIVE) return;

  const metadata = request.media.metadata as BPMetadata | undefined;
  const customData = metadata?.customData;
  if (!customData) return;

  const { mediaId, playlistId } = customData;
  if (!mediaId || !playlistId) return;

  Logger.info(LOG_TAG, 'Media ended — sending update_ended', {
    mediaId,
    playlistId,
  });

  Hermes.client
    .UpdateEnded({
      input: {
        media: mediaId,
        playlist: playlistId,
      },
    })
    .then(() => {
      Logger.debug(LOG_TAG, 'update_ended sent', { mediaId });
    })
    .catch((err: unknown) => {
      Logger.error(LOG_TAG, 'Failed to send update_ended', err);
    });
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

/**
 * Registers the media completion handler on the given player manager.
 *
 * Wires up:
 * - `ENDED` event → `completeMedia()` to notify Hermes
 *
 * @param playerManager - Cast player manager instance
 * @param session       - Session state (provides loadRequestData with mediaId/playlistId)
 */
export function registerMediaStatus(
  playerManager: PlayerManager,
  session: CastSession,
): void {
  playerManager.addEventListener(EventType.ENDED, () => {
    completeMedia(session);
    prefetchAndQueueNext(playerManager, session);
  });

  Logger.info(LOG_TAG, 'Media status handler registered');
}
